import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export interface IntegrationStatePanelProps {
	state: "loading" | "not_connected" | "error" | "empty";
	skeleton?: "cards" | "chart" | "rows";
	/** Optional activation-card headline; omitted for legacy consumers. */
	headline?: string;
	message?: string;
	/** Explains where the connection flow opens and what the user will do there. */
	feedforward?: string;
	/** An intentionally conservative estimate for setup and first-data availability. */
	eta?: string;
	ctaLabel?: string;
	onCta?: () => void;
	errorReason?: string;
	lastGood?: string;
	onRetry?: () => void;
}

export function IntegrationStatePanel({
	state,
	skeleton = "rows",
	headline,
	message,
	feedforward,
	eta,
	ctaLabel,
	onCta,
	errorReason,
	lastGood,
	onRetry,
}: IntegrationStatePanelProps) {
	if (state === "loading") {
		if (skeleton === "cards") {
			return (
				<div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<Skeleton key={i} className="h-[120px] rounded-xl" />
					))}
				</div>
			);
		}
		if (skeleton === "chart") {
			return <Skeleton className="h-[220px] w-full rounded-xl" />;
		}
		return (
			<div className="space-y-3 px-5 py-6">
				{Array.from({ length: 5 }).map((_, i) => (
					<Skeleton key={i} className="h-4 w-full" />
				))}
			</div>
		);
	}

	if (state === "error") {
		return (
			<div className="rounded-xl border border-destructive/20 bg-destructive/5 p-6 text-center">
				<div className="flex items-center justify-center gap-2">
					<AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
					<p className="text-dash-subtitle text-destructive">{errorReason ?? "Couldn't load data"}</p>
				</div>
				{lastGood && (
					<p className="mt-1 text-dash-context text-muted-foreground">
						Last good data: {lastGood}
					</p>
				)}
				{onRetry && (
					<Button
						variant="ghost"
						size="sm"
						onClick={onRetry}
						className="mt-2 min-h-11"
					>
						Retry
					</Button>
				)}
			</div>
		);
	}

	if (state === "not_connected") {
		return (
			<div className="rounded-xl border border-dashed border-warm-border-strong bg-warm-bg px-6 py-8 text-center">
				<div className="mx-auto max-w-2xl space-y-2">
					{headline && (
						<p className="text-dash-title font-semibold text-ink">{headline}</p>
					)}
					<p
						className={
							headline
								? "text-dash-subtitle text-ink-2"
								: "text-dash-subtitle font-medium text-foreground"
						}
					>
						{message}
					</p>
					{feedforward && (
						<p className="text-dash-subtitle text-muted-foreground">{feedforward}</p>
					)}
				</div>
				{ctaLabel && onCta && (
					<Button
						onClick={onCta}
						className="mt-5 min-h-11 bg-coral text-primary-foreground hover:bg-coral-hover"
					>
						{ctaLabel}
					</Button>
				)}
				{eta && (
					<p
						className={
							headline
								? "mx-auto mt-3.5 max-w-2xl text-dash-context text-ink-3"
								: "mx-auto mt-3 max-w-2xl text-dash-context text-muted-foreground"
						}
					>
						{eta}
					</p>
				)}
			</div>
		);
	}

	return (
		<p className="py-8 text-center text-dash-subtitle text-muted-foreground">
			{message ?? "No data available"}
		</p>
	);
}
